'use client';

import { Button } from '@/components/atoms';
import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum } from '@/domain/constants/constants';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface Props {
	matchupUuid: string;
	matchId: string;
	onCancel: () => void;
	onCancelMatchSuccess?: () => void;
}

export const CancelMatchModalContent = ({ onCancel, matchupUuid, matchId, onCancelMatchSuccess }: Props) => {
	const queryClient = useQueryClient();
	const { mutate: updateMatchMutation, isPending } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Match canceled successfully');
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupUuid) });
			onCancelMatchSuccess?.();
		}
	});

	const onConfirm = () => {
		updateMatchMutation({
			matchId: matchId,
			updateData: {
				matchStatus: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
				matchCompletedType: MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
			}
		});
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="relative w-full">
				<p className="text-black text-2xl font-medium">Cancel Match</p>
				<FontAwesomeIcon
					icon={faClose}
					className="cursor-pointer text-xl font-normal absolute right-0 top-1/2 -translate-y-1/2"
					onClick={onCancel}
				/>
			</div>
			<p className="text-gray-900 font-semibold text-sm">
				Are you sure you want to remove this match? Canceled matches are not be recognized for statistics.
			</p>
			<div className="flex gap-4 w-full justify-end">
				<Button
					className={`h-10 w-[7.5rem] border border-solid border-gray-300 bg-white text-sm font-semibold leading-5 !text-black hover:bg-gray-100`}
					onClick={onCancel}
				>
					Close
				</Button>
				<Button
					type="submit"
					onClick={onConfirm}
					disabled={isPending}
					className={`h-10 w-[7.5rem] bg-orange-500 text-sm font-semibold leading-5 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white`}
				>
					Save
				</Button>
			</div>
		</div>
	);
};
